/**
 * Order placed — the end of a tiffin checkout.
 *
 * Leads with the reference, since a guest customer has no other handle on the
 * order, then says plainly when and how the food arrives: "Lunch, 6 Oct, 12:30
 * PM – 2:30 PM, delivered to House 4…", or where to collect it. The draft is
 * cleared on the way out so "Order Another" starts clean.
 */

import { useEffect } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import {
  formatDayRange,
  formatMeals,
  formatMoney,
  formatPeople,
  formatWindow,
  serviceScheduleLabel,
} from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { Button } from '../../components/common/Button';
import { CheckIcon, Glyph } from '../../components/common/Icon';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { SupportCard } from '../../components/layout/SupportCard';
import { Text } from '../../components/common/Text';
import { useAppConfig, useTiffinOrder } from '../../core/api/hooks';
import { useTiffinStore } from './tiffinStore';
import { useHomeUnderneath } from '../../app/navigation/homeUnderneath';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';
import { useLocalized } from '../../core/i18n/content';

export function TiffinConfirmationScreen({
  navigation,
  route,
}: DonorScreenProps<'TiffinConfirmation'>) {
  const t = useT();
  const pick = useLocalized();
  const { orderId, publicReference } = route.params;
  const { data: order } = useTiffinOrder(orderId);
  const { data: config } = useAppConfig();
  const reset = useTiffinStore((s) => s.reset);

  // The order is placed; the draft has done its job.
  useEffect(() => () => reset(), [reset]);
  // The paid checkout leaves the history: back from here goes Home.
  useHomeUnderneath(navigation);

  const window = config?.tiffin?.slots.find((slot) => slot.slot === order?.serviceSlot);
  const delivery = order?.fulfilmentType === 'DOORSTEP_DELIVERY';
  const where = order
    ? delivery
      ? [order.address?.addressLine, order.address?.area, order.address?.city].filter(Boolean).join(', ')
      : order.pickupAddress ?? ''
    : '';

  return (
    <ScreenShell
      leading="none"
      testID="screen-tiffin-confirmation"
      pinnedFooter={
        <View style={styles.actions}>
          <Button
            label={t('Track Order')}
            onPress={() => navigation.navigate('TiffinOrder', { orderId })}
            leadingIcon={<Glyph name="tiffin" size={18} />}
            testID="tiffin-confirmation-track"
          />
          <Button
            label={t('Back to Home')}
            variant="secondary"
            onPress={() => navigation.navigate('Home')}
            leadingIcon={<Glyph name="home" size={18} />}
            testID="tiffin-confirmation-home"
          />
        </View>
      }
    >
      <View style={styles.hero}>
        <CheckIcon size={96} />
        <Text variant="screenTitle" tone="brand" align="center" style={styles.title}>
          {t('Order Placed!')}
        </Text>
        <Text variant="body" tone="secondary" align="center" style={styles.blurb}>
          {t('The kitchen has your order. We will keep you updated at every step.')}
        </Text>
      </View>

      <Pressable
        onPress={() => navigation.navigate('TiffinOrder', { orderId })}
        accessibilityRole="button"
        accessibilityLabel={`${t('Your order reference')} ${publicReference}. ${t('Tap to track your order.')}`}
        testID="tiffin-reference-card"
        style={({ pressed }) => [styles.referenceCard, pressed && styles.pressed]}
      >
        <Text variant="caption" tone="secondary" align="center">{t('Your order reference')}</Text>
        <Text variant="screenTitle" tone="brand" align="center" selectable testID="tiffin-reference">
          {publicReference}
        </Text>
        <Text variant="caption" tone="tertiary" align="center">{t('Tap to track your order.')}</Text>
      </Pressable>

      {order ? (
        <View style={styles.when} testID="tiffin-confirmation-when">
          <Glyph name={delivery ? 'delivery' : 'pickup'} size={28} />
          <View style={styles.whenText}>
            <Text variant="cardTitle" tone="brand">
              {`${t(order.serviceLabel)}, ${formatDayRange(order.serviceDate, order.endDate)}`}
            </Text>
            {window ? (
              <Text variant="bodyStrong" tone="brand" align="start">
                {formatWindow(window.start, window.end)}
              </Text>
            ) : null}
            <Text variant="caption" tone="secondary">
              {delivery ? `${t('Delivered to')} ${where}` : `${t('Collect from')} ${where}`}
            </Text>
            <Text variant="caption" tone="secondary">
              {pick(order.itemsPreview, order.itemsPreviewUr)}
            </Text>
            <Text variant="caption" tone="secondary">
              {`${formatPeople(order.persons)} · ${t(serviceScheduleLabel[order.schedule])} · ${formatMeals(order.deliveries)} · ${formatMoney(order.totalAmount)}`}
            </Text>
          </View>
        </View>
      ) : null}

      <SupportCard
        support={config?.support}
        title={t('Need help with this order?')}
        reference={publicReference}
      />
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  hero: {
    alignItems: 'center',
    gap: space.sm,
    paddingVertical: space.xl,
  },
  title: {
    marginTop: space.md,
  },
  blurb: {
    marginTop: space.xs,
    paddingHorizontal: space.md,
  },
  referenceCard: {
    gap: space.xs,
    padding: space.lg,
    borderRadius: radius.lg,
    backgroundColor: colors.bg.surface,
    borderWidth: 1,
    borderColor: colors.border.brand,
  },
  pressed: {
    opacity: 0.85,
  },
  when: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    marginTop: space.lg,
    marginBottom: space.lg,
    padding: space.lg,
    borderRadius: radius.lg,
    backgroundColor: colors.bg.brandSubtle,
  },
  whenText: {
    flex: 1,
    gap: space.xxs,
  },
  actions: {
    gap: space.sm,
  },
});
